import { useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useT } from "@/lib/i18n";

const CANCELLATION_REASON_KEYS = [
  "schedule_conflict",
  "feeling_unwell",
  "emergency",
  "changed_mind",
  "other",
] as const;

export type CancellationReason = (typeof CANCELLATION_REASON_KEYS)[number];

interface CancellationModalProps {
  isOpen: boolean;
  isLateCancellation: boolean;
  onConfirm: (reason?: CancellationReason) => void;
  onClose: () => void;
}

export function CancellationModal({
  isOpen,
  isLateCancellation,
  onConfirm,
  onClose,
}: CancellationModalProps) {
  const { t } = useT();
  const [selectedReason, setSelectedReason] =
    useState<CancellationReason | null>(null);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (isLateCancellation && !selectedReason) return;
    onConfirm(selectedReason ?? undefined);
  };

  return (
    <div className="modal modal-open z-[9999]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.15 }}
        className="modal-box relative card max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 pt-2 pb-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center bg-amber-500/15 text-amber-500">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h3 className="font-bold text-xl">
            {t("reservation.cancellation.areYouSure")}
          </h3>
        </div>

        {/* Message */}
        <div className="text-center text-dark-muted leading-relaxed px-2">
          <p>
            {isLateCancellation
              ? t("reservation.cancellation.lateWarning")
              : t("reservation.cancellation.regularWarning")}
          </p>
        </div>

        {/* Reason Picker (only for late cancellations) */}
        {isLateCancellation && (
          <div className="mt-5">
            <p className="text-xs font-semibold text-dark-muted uppercase tracking-wide mb-3">
              {t("reservation.cancellation.whyCancel")}
            </p>
            <div className="flex flex-col gap-2">
              {CANCELLATION_REASON_KEYS.map((reasonKey) => (
                <button
                  key={reasonKey}
                  onClick={() => setSelectedReason(reasonKey)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all text-sm ${
                    selectedReason === reasonKey
                      ? "border-red-500/50 bg-red-500/10"
                      : "border-dark-border bg-dark-card hover:bg-dark-hover"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedReason === reasonKey
                        ? "border-red-500 bg-red-500"
                        : "border-dark-border"
                    }`}
                  >
                    {selectedReason === reasonKey && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  {t(`reservation.cancellation.reasons.${reasonKey}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 mt-6">
          <button
            onClick={handleConfirm}
            disabled={isLateCancellation && !selectedReason}
            className={`btn w-full btn-danger ${
              isLateCancellation && !selectedReason
                ? "opacity-40 cursor-not-allowed"
                : ""
            }`}
          >
            {t("reservation.cancellation.cancelMyReservation")}
          </button>
          <button onClick={onClose} className="btn btn-ghost w-full">
            {t("reservation.cancellation.keepMyReservation")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
